import { SYMBOLS } from '@/constants';

export type TFormatNumberResult =
  | {
      isValid: false;

      rawValue?: never;
      formattedRawValue?: never;

      value?: never;
      formattedValue: string;

      shortValue?: never;
      formattedShortValue?: never;

      formattedShortValueUnit?: never;

      unit?: never;
      needTooltip?: never;
    }
  | {
      isValid: true;

      rawValue: number;
      formattedRawValue: string;

      value: number;
      formattedValue: string;

      shortValue: number;
      formattedShortValue: string;

      formattedShortValueUnit: string;

      unit: string | null;
      needTooltip: boolean;
    };

export type TNumberFormatterOptions = Intl.NumberFormatOptions & {
  fallback?: string;
  max?: number;
  min?: number;
  numberType?: 'number' | 'percent';
  roundFrom?: number;
};

const UNITS: { unitCount: number; unitName: string }[] = [
  { unitCount: 1e18, unitName: 'квинтлн.' }, // Квинтиллион
  { unitCount: 1e15, unitName: 'квадрлн.' }, // Квадриллион
  { unitCount: 1e12, unitName: 'трлн.' }, // Триллионы
  { unitCount: 1e9, unitName: 'млрд.' }, // Миллиарды
  { unitCount: 1e6, unitName: 'млн.' }, // Миллионы
  { unitCount: 1e3, unitName: 'тыс.' } // Тысячи
];

/**
 * Форматирует число и возвращает объект с различными представлениями значения.
 *
 * @param value - Значение для форматирования. Принимает любой тип; невалидные значения обрабатываются через `fallback`.
 * @param options - Опции форматирования.
 * @param options.fallback - Строка, возвращаемая в `formattedValue` при невалидном значении. По умолчанию `'—'`.
 * @param options.numberType - Тип числа: `'number'` (по умолчанию) или `'percent'` (зажимает значение в диапазон 0–100).
 * @param options.roundFrom - Порог (включительно), начиная с которого применяется сокращение числа (тыс., млн. и т.д.). По умолчанию `1 000` (тысячи).
 * @param options.maximumFractionDigits - Максимальное количество знаков после запятой. По умолчанию `2`.
 * @param options.roundingMode - Режим округления (`Intl.NumberFormatOptions`). По умолчанию `'halfExpand'`.
 *
 * @returns При невалидном значении (`isValid: false`):
 * - `isValid` — `false`
 * - `formattedValue` — строка-заглушка (`fallback`)
 *
 * При валидном значении (`isValid: true`):
 * - `isValid` — `true`
 * - `rawValue` — исходное число до округления
 * - `formattedRawValue` — строковое представление `rawValue` без округления (до 20 знаков после запятой)
 * - `value` — округлённое число
 * - `formattedValue` — строковое представление `value` с пробелами-разделителями тысяч
 * - `shortValue` — сокращённое число (например, `12.35` для `12 350 000`); совпадает с `value`, если сокращение не применялось
 * - `formattedShortValue` — строковое представление `shortValue`
 * - `formattedShortValueUnit` — строка с сокращённым числом и единицей измерения (например, `'12.35 млн.'`); совпадает с `formattedValue`, если сокращение не применялось
 * - `unit` — единица измерения (`'тыс.'`, `'млн.'`, `'млрд.'`, `'трлн.'`) или `null`, если сокращение не применялось
 * - `needTooltip` — `true`, если значение было округлено или сокращено (рекомендуется показывать tooltip с полным значением)
 *
 * @example
 * formatNumber("не число");
 * // { isValid: false, formattedValue: '—' }
 *
 * @example
 * formatNumber(12345678.546);
 * // {
 * //   formattedRawValue: '12 345 678.546',
 * //   formattedShortValue: '12.35',
 * //   formattedShortValueUnit: '12.35 млн.',
 * //   formattedValue: '12 345 678.55',
 * //   isValid: true,
 * //   needTooltip: true,
 * //   rawValue: 12345678.546,
 * //   shortValue: 12.35,
 * //   unit: 'млн.',
 * //   value: 12345678.55,
 * // }
 *
 * @example
 * formatNumber(78.546, { numberType: 'percent' });
 * // {
 * //   formattedRawValue: '78.546',
 * //   formattedShortValue: '78.55',
 * //   formattedShortValueUnit: '78.55',
 * //   formattedValue: '78.55',
 * //   isValid: true,
 * //   needTooltip: true,
 * //   rawValue: 78.546,
 * //   shortValue: 78.55,
 * //   unit: null,
 * //   value: 78.55,
 * // }
 *
 * @example
 * formatNumber(123.4);
 * // {
 * //   formattedRawValue: '123.4',
 * //   formattedShortValue: '123.4',
 * //   formattedShortValueUnit: '123.4',
 * //   formattedValue: '123.4',
 * //   isValid: true,
 * //   needTooltip: false,
 * //   rawValue: 123.4,
 * //   shortValue: 123.4,
 * //   unit: null,
 * //   value: 123.4,
 * // }
 */
export const formatNumber = (
  value: unknown,
  options?: TNumberFormatterOptions
): TFormatNumberResult => {
  const {
    fallback = SYMBOLS.longDash,
    max,
    maximumFractionDigits = 2,
    min,
    numberType = 'number',
    roundFrom = UNITS[UNITS.length - 1].unitCount,
    roundingMode = 'halfExpand',
    ...intlOptions
  } = options || {};

  try {
    if (
      (typeof value === 'string' && value.trim() === '') ||
      (typeof value !== 'number' && typeof value !== 'string')
    ) {
      return {
        isValid: false,
        formattedValue: fallback
      };
    }

    let numberValue: number = Number(value);

    if (typeof value === 'string') {
      numberValue = Number(value.replace(/\s/g, ''));
    }

    if (Number.isNaN(numberValue) || !Number.isFinite(numberValue)) {
      return {
        isValid: false,
        formattedValue: fallback
      };
    }

    const isPercent: boolean = numberType === 'percent';
    const maxLimit: number | null = isPercent ? (max ?? 100) : (max ?? null);
    const minLimit: number | null = isPercent ? (min ?? 0) : (min ?? null);

    if (minLimit !== null) numberValue = Math.max(numberValue, minLimit);
    if (maxLimit !== null) numberValue = Math.min(numberValue, maxLimit);

    const rawFormatter = new Intl.NumberFormat('ru-KZ', {
      maximumFractionDigits: 20
    });
    const formatter = new Intl.NumberFormat('ru-KZ', {
      ...intlOptions,
      roundingMode,
      maximumFractionDigits
    });

    const formatValue = (
      value: number,
      _formatter: Intl.NumberFormat = formatter
    ): string => {
      return _formatter.format(value).replace(',', '.');
    };

    const formattedValue = formatValue(numberValue);
    const newNumberValue = Number(formattedValue.replace(/\s/g, ''));

    const result: TFormatNumberResult = {
      isValid: true,

      rawValue: numberValue,
      formattedRawValue: formatValue(numberValue, rawFormatter),

      value: newNumberValue,
      formattedValue: formattedValue,

      shortValue: newNumberValue,
      formattedShortValue: formattedValue,

      formattedShortValueUnit: formattedValue,

      unit: null,
      needTooltip: numberValue !== newNumberValue
    };

    const absNumberValue = Math.abs(newNumberValue);

    if (absNumberValue >= roundFrom) {
      for (const { unitCount, unitName } of UNITS) {
        if (absNumberValue >= unitCount) {
          const dividedValue = newNumberValue / unitCount;

          const formattedShortValue = formatValue(dividedValue);

          result.shortValue = Number(formattedShortValue.replace(/\s/g, ''));
          result.formattedShortValue = formattedShortValue;

          result.formattedShortValueUnit = `${formattedShortValue} ${unitName}`;

          result.unit = unitName;
          result.needTooltip = true;
          break;
        }
      }
    }

    return result;
  } catch {
    return {
      isValid: false,
      formattedValue: fallback
    };
  }
};

export const createNumberFormatter = (options?: TNumberFormatterOptions) => {
  return (value: unknown) => formatNumber(value, options);
};

type TNumberFieldValue = string | number | null | undefined;

// Дробное число без знака, разделитель ',' или '.'
export const DECIMAL_PATTERN = /^\d+([.,]\d+)?$/;

/**
 * Очищает ввод дробного числа: оставляет цифры и один десятичный разделитель,
 * запятая заменяется на точку.
 *
 * @param value - Введённое значение.
 * @param maxFractionDigits - Максимальное количество знаков после разделителя. По умолчанию без ограничения.
 *
 * @example
 * sanitizeDecimalInput('1,6 л'); // '1.6'
 * sanitizeDecimalInput('12.345', 2); // '12.34'
 */
export const sanitizeDecimalInput = (
  value: string,
  maxFractionDigits?: number
): string => {
  const [integerPart, ...fractionParts] = value
    .replace(/,/g, '.')
    .replace(/[^\d.]/g, '')
    .split('.');
  if (fractionParts.length === 0) return integerPart;
  const fractionPart = fractionParts.join('');
  return `${integerPart}.${maxFractionDigits === undefined ? fractionPart : fractionPart.slice(0, maxFractionDigits)}`;
};

/**
 * Проверяет значение поля по шаблону. Пустое значение считается валидным,
 * обязательность проверяется через `required`.
 */
export const isNumberFieldValid = (
  value: TNumberFieldValue,
  pattern: RegExp
): boolean => {
  const stringValue = String(value ?? '').trim();
  return !stringValue || pattern.test(stringValue);
};

/**
 * Преобразует значение поля в число, запятая заменяется на точку.
 * Пустое или нечисловое значение → `undefined` (а не `0`).
 */
export const toOptionalNumber = (
  value: TNumberFieldValue
): number | undefined => {
  const stringValue = String(value ?? '')
    .trim()
    .replace(',', '.');
  if (!stringValue) return undefined;
  const numberValue = Number(stringValue);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

/**
 * Преобразует значение обязательного (уже провалидированного) поля в число.
 */
export const toNumber = (value: TNumberFieldValue): number =>
  toOptionalNumber(value) ?? 0;
